/**
 * Unified typed form for all intake encounters (Wellness / CoCM / Unbilled).
 *
 * Steps: Topics → Assessments → Goals → Notes (scribe + clinical fields) →
 * Details → optional Enrollment → optional Referrals → (Next-Task Assignment
 * when activity-driven).
 *
 * The persisted encounter-type code + billable status (the "billing reality")
 * are PREFILLED from the patient's scheduled eligibility `meta.tag` (new
 * encounters) or the existing encounter's tag (edit), and are then selected by
 * the clinician in the Billing section of the Details step — placed directly
 * above the care-time log, so the billing track is confirmed right before the
 * time it applies to is logged. Eligibility is advisory, and the reality may
 * diverge. A Wellness intake produces a 90791 ChargeItem on save. Goals are
 * always available. There is no CoCM cross-prefill.
 *
 * @agentic connections
 */

import { Alert, Anchor, Skeleton, Stack } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useMedplum } from "@medplum/react";
import type { Encounter } from "@medplum/fhirtypes";

import {
  COCM_INTAKE_ENCOUNTER_TYPE_CODE,
  getCarePlanActivityDisplay,
  MSE_PHONE_NOT_INFERABLE_DEFAULT,
  resolveBillableFromBillingFlag,
  resolveIntakeCodeFromBillingFlag,
  VALUE_SET,
  type IntakeBillingFlagCode,
} from "@benchmark/core/constants";

import {
  FormField,
  FormStep,
  SteppedForm,
} from "@benchmark/ui/components/atomic";
import { GoalCreationSection } from "@/components/shared/forms/GoalCreationSection";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { getPatientIntakeEligibility } from "@benchmark/core/resolvers";
import { findRecentDuplicateIntakeEncounter } from "@benchmark/core/services";
import type {
  EncounterActivityContext,
  IntakeInteractionFormData,
} from "@benchmark/core/types";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";

import { IntakeGuideSection } from "../sections/IntakeGuideSection";
import { IntakeCareTimeSection } from "../sections/IntakeCareTimeSection";
import {
  IntakeEligibilityBanner,
  IntakeBillingRealitySelect,
} from "../sections/IntakeBillingRealitySection";
import { ClinicalNotesStep } from "../shared/ClinicalNotesStep";
import {
  AssessmentsStep,
  validateAssessmentsStep,
} from "../shared/AssessmentsStep";
import { isUnderEighteen } from "../note-sections";
import { EnrollmentStep } from "../activity-chain/EnrollmentStep";
import { ReferralsStep } from "../activity-chain/ReferralsStep";
import type { EncounterFormRef } from "../EncounterFormShell";
import { useIntakeFormScaffold } from "../hooks/useIntakeFormScaffold";
import type { IntakeEncounterCode } from "../hooks/useIntakeFormScaffold";
import { useScribePipeline } from "../hooks/useScribePipeline";
import { EncounterDetailsStep } from "../shared/EncounterDetailsStep";
import { NextTaskAssignmentStep } from "../shared/NextTaskAssignmentStep";
import {
  buildDirectionOptions,
  buildMethodOptions,
} from "../shared/encounterIcons";
import { useIntakeInteractionSubmit } from "./useIntakeInteractionSubmit";

const METHOD_OPTIONS = buildMethodOptions(
  (code) => code === "phone" || code === "video",
);
const DIRECTION_OPTIONS = buildDirectionOptions();

const INTAKE_CODES: IntakeEncounterCode[] = [
  "clinical-intake-interaction",
  "cocm-intake-interaction",
  "unbilled-intake-interaction",
];

/** Reverse-map a persisted intake code to its display billing flag. */
const codeToBillingFlag = (code: string): IntakeBillingFlagCode =>
  code === "cocm-intake-interaction"
    ? "cocm"
    : code === "clinical-intake-interaction"
      ? "wellness"
      : "no-bill";

/** Read the intake encounter-type tag from an existing encounter. */
const readIntakeTypeTag = (
  encounter: Encounter | undefined,
): IntakeEncounterCode | undefined => {
  const code = encounter?.meta?.tag?.find(
    (t) => t.system === VALUE_SET.ENCOUNTER_TYPE,
  )?.code;
  return code && (INTAKE_CODES as string[]).includes(code)
    ? (code as IntakeEncounterCode)
    : undefined;
};

/**
 * Props for `IntakeInteractionForm`.
 * @agentic connections
 */
export type IntakeInteractionFormProps = {
  patientId?: string;
  encounter?: Encounter;
  onSuccess?: (encounterId: string) => void;
  onCancel?: () => void;
  activityContext?: EncounterActivityContext;
  restrictToEncounterTypes?: string[];
  nextActivityLabel?: string;
  nextActivityDefaultOwner?: { ref: string; display: string };
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  onNextTaskOwnerOverrideChange?: (
    owner: { ref: string; display: string } | null,
  ) => void;
  nextActivityAppliedRule?: AppliedBusinessRule;
  isResolvingNextTaskOwner?: boolean;
};

/**
 * Unified intake form for `clinical-`, `cocm-`, and `unbilled-intake-interaction`.
 * @agentic connections
 */
export const IntakeInteractionForm = forwardRef<
  EncounterFormRef,
  IntakeInteractionFormProps
>(function IntakeInteractionForm(
  {
    patientId: propPatientId,
    encounter: propEncounter,
    onSuccess,
    onCancel,
    activityContext,
    restrictToEncounterTypes,
    nextActivityLabel,
    nextActivityDefaultOwner,
    nextTaskOwnerOverride,
    onNextTaskOwnerOverrideChange,
    nextActivityAppliedRule,
    isResolvingNextTaskOwner = false,
  },
  ref,
) {
  // Scaffold hydration seeds the encounter-type from this code. For edit we
  // seed from the existing tag; a derivation effect below corrects both the
  // code (new: billing flag; edit: fetched tag) and billable status.
  const scaffoldType: IntakeEncounterCode =
    readIntakeTypeTag(propEncounter) ?? "unbilled-intake-interaction";

  const form = useForm<IntakeInteractionFormData>({
    initialValues: {
      "encounter-type": scaffoldType,
      period: { start: "", end: "" },
      "encounter-method": "",
      "encounter-inout": "",
      selectedQuestionnaires: [],
      questionnaireResponses: {},
      "intake-scratch": "",
      "mental-status-appearance": MSE_PHONE_NOT_INFERABLE_DEFAULT,
      "mental-status-perception": MSE_PHONE_NOT_INFERABLE_DEFAULT,
      _topicChecks: {},
      _scribeApplied: false,
      _scribeInitialized: false,
    },
  });

  const {
    patient,
    existingEncounter,
    isEditing,
    isLoadingEdit,
    loadError,
    practitionerRoles,
    ConfirmationModal,
    markAsClean,
    discardDraft,
    originalFormData,
    setOriginalFormData,
  } = useIntakeFormScaffold({
    type: scaffoldType,
    form,
    propPatientId,
    propEncounter,
    activityContext,
    nextTaskOwnerOverride,
    ref,
  });

  const scribe = useScribePipeline(form);

  // Duplicate-intake guardrail (FR-C4): warn when a finished intake for this
  // patient exists within the last 24h, excluding the encounter being edited.
  const medplum = useMedplum();
  const [duplicateIntake, setDuplicateIntake] = useState<
    Encounter | undefined
  >();
  useEffect(() => {
    const patientId = patient?.id;
    // Only warn when starting a NEW intake; editing/resuming an existing
    // encounter is not a duplicate of itself.
    if (!patientId || existingEncounter?.id) {
      setDuplicateIntake(undefined);
      return;
    }
    let cancelled = false;
    findRecentDuplicateIntakeEncounter(medplum, patientId, {
      withinHours: 24,
    })
      .then((encounter) => {
        if (!cancelled) setDuplicateIntake(encounter);
      })
      .catch(() => {
        if (!cancelled) setDuplicateIntake(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, patient?.id, existingEncounter?.id]);

  const isDraftResume = existingEncounter?.status === "in-progress";
  const showSaveDraftButton = !!activityContext;

  const showCareTime = activityContext?.activityCode === "complete-intake";
  const careTimeTaskId = activityContext?.taskId;
  const careTimeCarePlanId = activityContext?.carePlanId;
  useEffect(() => {
    if (!showCareTime) return;
    if (careTimeTaskId) {
      form.setFieldValue("_careTimeTaskId" as never, careTimeTaskId as never);
    }
    if (careTimeCarePlanId) {
      form.setFieldValue(
        "_careTimeCarePlanId" as never,
        careTimeCarePlanId as never,
      );
    }
    // `form` intentionally omitted (new object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCareTime, careTimeTaskId, careTimeCarePlanId]);

  // Billing reality: the persisted encounter-type + billable are PREFILLED from
  // the patient's scheduled eligibility (a `meta.tag`, already loaded on the
  // patient), then chosen by the clinician at complete-intake — never derived
  // read-only. Edits seed from the existing encounter's tag.
  const eligibility = getPatientIntakeEligibility(patient);
  // Pediatric section scratch (call guide) and pediatric note sections gate on
  // age < 18 (D10), computed against the encounter start when available.
  const isPediatric = isUnderEighteen(
    patient?.birthDate,
    existingEncounter?.period?.start ?? form.values.period?.start,
  );
  // Whether the one-time prefill has run. Synchronous (no async read), so it can
  // never stick: both branches set it unconditionally once the patient loads.
  const [prefilled, setPrefilled] = useState(false);

  // Seed the Enrollment step with CoCM enroll-now when the billing selection is
  // CoCM, but never override a program the clinician already chose (FR-H2.2).
  const maybeSeedCoCMEnrollment = (code: string) => {
    if (code !== COCM_INTAKE_ENCOUNTER_TYPE_CODE) return;
    if (form.values["followup-program-selection"]?.selectedProgram) return;
    form.setFieldValue("followup-enroll" as never, "enroll-now" as never);
    form.setFieldValue(
      "followup-program-selection" as never,
      {
        selectedProgram: "cocm",
        reasonCodes: [],
        reasonText: "",
        selectedNeeds: [],
        serviceNeeds: [],
      } as never,
    );
  };

  useEffect(() => {
    if (prefilled) return;
    if (!patient) return;

    if (existingEncounter) {
      // Edit: mirror the existing encounter's tag; if absent, keep the seeded
      // default rather than leave the form gated forever.
      const tagCode = readIntakeTypeTag(existingEncounter);
      if (tagCode) {
        form.setFieldValue("encounter-type", tagCode);
        form.setFieldValue(
          "encounter-billable",
          resolveBillableFromBillingFlag(codeToBillingFlag(tagCode)),
        );
      }
      setPrefilled(true);
      return;
    }

    // A single restricted encounter type (the billing selector is hidden in
    // this case) is authoritative — never overwrite it with the
    // eligibility-derived type.
    if (restrictToEncounterTypes && restrictToEncounterTypes.length === 1) {
      const forced = restrictToEncounterTypes[0] as IntakeEncounterCode;
      form.setFieldValue("encounter-type", forced);
      form.setFieldValue(
        "encounter-billable",
        resolveBillableFromBillingFlag(codeToBillingFlag(forced)),
      );
      maybeSeedCoCMEnrollment(forced);
      setPrefilled(true);
      return;
    }

    // New: prefill from eligibility (undefined → safe no-bill/unbilled default).
    const prefilledCode = resolveIntakeCodeFromBillingFlag(
      eligibility,
    ) as IntakeEncounterCode;
    form.setFieldValue("encounter-type", prefilledCode);
    form.setFieldValue(
      "encounter-billable",
      resolveBillableFromBillingFlag(eligibility),
    );
    // A CoCM-eligible patient whose form auto-selects CoCM gets the same
    // enroll-now pre-fill as a manual CoCM selection (FR-H2).
    maybeSeedCoCMEnrollment(prefilledCode);
    setPrefilled(true);
    // `form` intentionally omitted (new object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prefilled,
    patient,
    existingEncounter,
    eligibility,
    restrictToEncounterTypes,
  ]);

  // Keep `encounter-billable` in sync when the clinician changes the reality.
  const handleBillingRealityChange = (code: string) => {
    if (!(INTAKE_CODES as string[]).includes(code)) return;
    form.setFieldValue("encounter-type", code as IntakeEncounterCode);
    form.setFieldValue(
      "encounter-billable",
      resolveBillableFromBillingFlag(codeToBillingFlag(code)),
    );
    maybeSeedCoCMEnrollment(code);
  };

  const { handleSubmit, handleDraftSave } = useIntakeInteractionSubmit({
    patient,
    existingEncounter,
    originalFormData,
    setOriginalFormData,
    practitionerRoles,
    activityContext,
    onSuccess,
    onSubmitted: markAsClean,
    discardDraft,
  });

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();
  const [isDraftSaving, setIsDraftSaving] = useState(false);

  const wrappedDraftSave = async () => {
    setIsDraftSaving(true);
    try {
      await handleDraftSave(form.values);
    } finally {
      setIsDraftSaving(false);
    }
  };

  const shouldShowEnrollmentStep =
    activityContext?.activityCode === "complete-intake";

  // The billing-reality selector is hidden when the form is restricted to a
  // single encounter type (the type is then fixed and not clinician-chosen).
  const showBillingSelect = !(
    restrictToEncounterTypes && restrictToEncounterTypes.length === 1
  );

  const steps = useMemo(
    () => [
      {
        id: "intake-guide",
        title: "Topics",
        subtitle: "Conversation guide and scratch pad for encounter calls.",
      },
      {
        id: "assessments",
        title: "Assessments",
        subtitle: "Administer standardized assessments.",
        optional: true,
        validate: validateAssessmentsStep,
      },
      {
        id: "notes",
        title: "Notes",
        subtitle: "Document the encounter",
      },
      {
        id: "details",
        title: "Details",
        subtitle: "Review and confirm the interaction details.",
        requiredFields: [
          "encounter-type",
          "period.start",
          "period.end",
          "encounter-method",
          "encounter-inout",
        ] as string[],
      },
      ...(shouldShowEnrollmentStep
        ? [
            {
              id: "enrollment",
              title: "Enrollment",
              subtitle: "Select an operational program for this member.",
              validate: (values: IntakeInteractionFormData) => {
                const errors: Record<string, string> = {};
                if (!values["followup-enroll"]) {
                  errors["followup-program-selection"] =
                    "Select a care plan or choose Enroll Later";
                  return errors;
                }
                if (
                  values["followup-enroll"] === "enroll-now" &&
                  !values["followup-program-selection"]?.selectedProgram
                ) {
                  errors["followup-program-selection"] =
                    "Select a care plan or choose Enroll Later";
                }
                return Object.keys(errors).length > 0 ? errors : null;
              },
            },
            {
              id: "referrals",
              title: "Referrals",
              subtitle: "Add service needs for this member.",
              showWhen: (values: IntakeInteractionFormData) => {
                const program =
                  values["followup-program-selection"]?.selectedProgram;
                if (program === "triage") return true;
                if (program === "cocm") {
                  return values["followup-include-referrals"] === true;
                }
                return false;
              },
              validate: (values: IntakeInteractionFormData) => {
                const errors: Record<string, string> = {};
                const needs = values["followup-service-needs"] ?? [];
                const selectedProgram =
                  values["followup-program-selection"]?.selectedProgram;
                const requiresAtLeastOneNeed = selectedProgram === "triage";
                if (requiresAtLeastOneNeed && needs.length === 0) {
                  errors["followup-service-needs"] =
                    "At least one service need is required for this program";
                } else if (needs.length > 0) {
                  const missingType = needs.find((n) => !n.serviceType);
                  if (missingType) {
                    errors["followup-service-needs"] =
                      "Each service need must have a service type selected";
                  } else {
                    const missingMode = needs.find((n) => n.modes.length === 0);
                    if (missingMode) {
                      errors["followup-service-needs"] =
                        "Each service need must have at least one mode selected";
                    }
                  }
                }
                return Object.keys(errors).length > 0 ? errors : null;
              },
            },
          ]
        : []),
      ...(activityContext
        ? [
            {
              id: "next-task-assignment",
              title: "Assignment",
              subtitle: "Review next task assignment",
            },
          ]
        : []),
    ],
    [activityContext, isEditing, shouldShowEnrollmentStep],
  );

  const submitText = isDraftResume
    ? "Complete Encounter"
    : activityContext?.activityCode
      ? getCarePlanActivityDisplay(activityContext.activityCode)
      : isEditing
        ? "Update Interaction"
        : "Save Interaction";

  if (loadError) {
    return (
      <div data-testid="intake-interaction-form">
        <Alert
          color="red"
          icon={<IconAlertTriangle size={18} />}
          title="Couldn't load this intake"
          data-testid="intake-load-error"
        >
          We couldn&apos;t load the saved details for this intake. Refresh the
          page to try again — your previous documentation has not been changed.
        </Alert>
        <ConfirmationModal />
      </div>
    );
  }

  if (!patient || isLoadingEdit || !prefilled) {
    return (
      <div data-testid="intake-interaction-form">
        <Stack gap="md">
          <Skeleton height={32} width="40%" />
          <Skeleton height={36} />
          <Skeleton height={44} width="200px" />
        </Stack>
        <ConfirmationModal />
      </div>
    );
  }

  return (
    <div data-testid="intake-interaction-form">
      {duplicateIntake && (
        <Alert
          color="orange"
          icon={<IconAlertTriangle size={18} />}
          title="Possible duplicate intake"
          mb="md"
          data-testid="duplicate-intake-warning"
        >
          A finished intake for this patient was recorded in the last 24 hours.
          Completing another may duplicate documentation.{" "}
          {patient?.id && (
            <Anchor component={Link} to={`/Patient/${patient.id}`}>
              View the patient&apos;s encounters
            </Anchor>
          )}
        </Alert>
      )}
      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={createSubmissionHandler(handleSubmit)}
        isSubmitting={isSubmitting}
        submitText={submitText}
        onCancel={onCancel}
        onSaveDraft={showSaveDraftButton ? wrappedDraftSave : undefined}
        isDraftSaving={isDraftSaving}
        allowFreeNavigation={isDraftResume}
      >
        <FormStep id="intake-guide">
          <IntakeGuideSection
            encounterType={form.values["encounter-type"]}
            isPediatric={isPediatric}
            sectionScratch={form.values._sectionScratch ?? {}}
            onSectionScratchChange={(sectionId, value) =>
              form.setFieldValue(
                "_sectionScratch" as never,
                {
                  ...(form.values._sectionScratch ?? {}),
                  [sectionId]: value,
                } as never,
              )
            }
            generalScratch={form.values["intake-scratch"] ?? ""}
            onGeneralScratchChange={(value) =>
              form.setFieldValue("intake-scratch" as never, value as never)
            }
            goalsSlot={<GoalCreationSection form={form} />}
          />
        </FormStep>

        <FormStep id="assessments">
          <AssessmentsStep
            form={form}
            patientBirthDate={patient?.birthDate}
            patientId={patient?.id}
            currentEncounterId={
              existingEncounter?.id ?? form.values._scribeEncounterId
            }
            currentEncounterStatus={existingEncounter?.status}
          />
        </FormStep>

        <FormStep id="notes">
          <ClinicalNotesStep
            form={form}
            patient={patient}
            scribe={scribe}
            existingEncounter={existingEncounter}
          />
        </FormStep>

        <FormStep id="details">
          <Stack gap="md">
            <EncounterDetailsStep
              form={form}
              methodOptions={METHOD_OPTIONS}
              directionOptions={DIRECTION_OPTIONS}
              billableOptions={[]}
              requiresBillable={false}
            />
            {/* Billing eligibility advisory + reality selector sit directly
                above the care-time log (not at the top of the step), so the
                clinician confirms the billing track right before logging the
                time it applies to. */}
            <IntakeEligibilityBanner eligibility={eligibility} />
            {showBillingSelect ? (
              <FormField label="Billing" variant="section" showDivider={false}>
                <IntakeBillingRealitySelect
                  value={form.values["encounter-type"]}
                  onChange={handleBillingRealityChange}
                />
              </FormField>
            ) : null}
            {showCareTime ? (
              <IntakeCareTimeSection form={form} patientId={patient?.id} />
            ) : null}
          </Stack>
        </FormStep>

        <FormStep id="enrollment">
          <EnrollmentStep form={form} />
        </FormStep>

        <FormStep id="referrals">
          <ReferralsStep form={form} />
        </FormStep>

        <FormStep id="next-task-assignment">
          <NextTaskAssignmentStep
            form={form}
            nextActivityLabel={nextActivityLabel}
            nextActivityDefaultOwner={nextActivityDefaultOwner}
            nextTaskOwnerOverride={nextTaskOwnerOverride}
            onNextTaskOwnerOverrideChange={onNextTaskOwnerOverrideChange}
            nextActivityAppliedRule={nextActivityAppliedRule}
            isResolvingNextTaskOwner={isResolvingNextTaskOwner}
          />
        </FormStep>
      </SteppedForm>

      <ConfirmationModal />
    </div>
  );
});
